import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// Chakra imports
import {
  Box,
  Button,
  Flex,
  Text,
  useColorModeValue,
  useToast,
  Spinner,
  Center,
  Badge,
  Divider,
  Icon,
  SimpleGrid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';

// Custom components
import Card from 'components/card/Card.js';
import PaymentsTable from '../components/PaymentsTable';

// Services
import tuitionService from 'services/tuitionService';
import { formatCurrency, formatDate, formatStatus } from 'utils/format';

// Icons
import { MdArrowBack, MdSchool, MdAttachMoney, MdCalendarToday, MdPerson, MdPayment, MdCheckCircle, MdPendingActions, MdEdit, MdDelete, MdAdd } from 'react-icons/md';

export default function ViewTuition() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  // Chakra Color Mode
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary = useColorModeValue('secondaryGray.600', 'white');
  const bg = useColorModeValue('white', 'navy.800');
  const borderColor = useColorModeValue('secondaryGray.200', 'whiteAlpha.100');
  const bgStatus = useColorModeValue('secondaryGray.100', 'whiteAlpha.100');
  const paymentInfoBg = useColorModeValue('blue.50', 'blue.900');
  const paymentInfoBorder = useColorModeValue('blue.200', 'blue.700');

  // State
  const [tuition, setTuition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    amount: '',
    due_date: '',
    prepa_session: '',
    type: '',
    description: '',
    status: '',
  });
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [paymentFormData, setPaymentFormData] = useState({
    amount: '',
    payment_date: '',
    payment_method: '',
    reference_number: '',
    notes: '',
    status: 'pending',
  });
  const [paymentErrors, setPaymentErrors] = useState({});
  const [creatingPayment, setCreatingPayment] = useState(false);
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  
  // Modals
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { isOpen: isPaymentOpen, onOpen: onPaymentOpen, onClose: onPaymentClose } = useDisclosure();
  const cancelRef = useRef();

  // Fetch tuition data
  useEffect(() => {
    const fetchTuition = async () => {
      if (!id) {
        setError('Tuition ID is required');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const result = await tuitionService.getTuition(id);

        if (result.success && result.data) {
          setTuition(result.data);
        } else {
          setError(result.error || 'Failed to load tuition');
          toast({
            title: 'Error',
            description: result.error || 'Failed to load tuition',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      } catch (err) {
        console.error('Error fetching tuition:', err);
        setError('Failed to load tuition. Please try again.');
        toast({
          title: 'Error',
          description: 'Failed to load tuition. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchTuition();
  }, [id, toast]);

  // Fetch payments data
  useEffect(() => {
    const fetchPayments = async () => {
      if (!id) return;

      setPaymentsLoading(true);
      try {
        const result = await tuitionService.getPayments({
          tuition_id: id,
          skip: 0,
          limit: 100, // Get all payments for this tuition
        });

        if (result.success && result.data) {
          setPayments(result.data);
        } else {
          console.error('Failed to load payments:', result.error);
        }
      } catch (err) {
        console.error('Error fetching payments:', err);
      } finally {
        setPaymentsLoading(false);
      }
    };

    if (id) {
      fetchPayments();
    }
  }, [id]);

  // Initialize form data when tuition is loaded
  useEffect(() => {
    if (tuition) {
      setFormData({
        amount: tuition.amount || '',
        due_date: tuition.due_date ? tuition.due_date.split('T')[0] : '',
        prepa_session: tuition.prepa_session || '',
        type: tuition.type || '',
        description: tuition.description || '',
        status: tuition.status || '',
      });
    }
  }, [tuition]);

  // Handle edit button click
  const handleEditClick = () => {
    onEditOpen();
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle update tuition
  const handleUpdate = async () => {
    setUpdating(true);
    try {
      // Convert date to ISO format
      const updateData = {
        ...formData,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : undefined,
        amount: formData.amount ? Number(formData.amount) : undefined,
      };

      // Remove empty fields
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === '' || updateData[key] === undefined) {
          delete updateData[key];
        }
      });

      const result = await tuitionService.updateTuitionPartial(id, updateData);

      if (result.success) {
        toast({
          title: 'Success',
          description: result.message || 'Tuition updated successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        onEditClose();
        // Refresh tuition data
        const fetchResult = await tuitionService.getTuition(id);
        if (fetchResult.success && fetchResult.data) {
          setTuition(fetchResult.data);
        }
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to update tuition',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Update error:', err);
      toast({
        title: 'Error',
        description: 'Failed to update tuition. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdating(false);
    }
  };

  // Handle delete tuition
  const handleDelete = async () => {
    setDeleting(true);
    try {
      const result = await tuitionService.deleteTuition(id);

      if (result.success) {
        toast({
          title: 'Success',
          description: result.message || 'Tuition deleted successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        onDeleteClose();
        // Navigate back to tuitions list
        navigate('/admin/tuition');
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to delete tuition',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Delete error:', err);
      toast({
        title: 'Error',
        description: 'Failed to delete tuition. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDeleting(false);
    }
  };

  // Handle payment form input change
  const handlePaymentInputChange = (e) => {
    const { name, value } = e.target;
    setPaymentFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field
    if (paymentErrors[name]) {
      setPaymentErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Handle create payment
  const handleCreatePayment = async () => {
    // Reset errors
    setPaymentErrors({});

    // Validation
    const errors = {};
    if (!paymentFormData.amount || paymentFormData.amount <= 0) {
      errors.amount = 'Amount is required and must be greater than 0';
    } else if (tuition && paymentFormData.amount > tuition.remaining_balance) {
      errors.amount = `Amount cannot exceed remaining balance of ${formatCurrency(tuition.remaining_balance)}`;
    }
    if (!paymentFormData.payment_date) {
      errors.payment_date = 'Payment date is required';
    }
    if (!paymentFormData.payment_method) {
      errors.payment_method = 'Payment method is required';
    }

    if (Object.keys(errors).length > 0) {
      setPaymentErrors(errors);
      return;
    }

    setCreatingPayment(true);
    try {
      const paymentData = {
        tuition_id: id,
        amount: Number(paymentFormData.amount),
        payment_date: new Date(paymentFormData.payment_date).toISOString(),
        payment_method: paymentFormData.payment_method,
        status: paymentFormData.status,
      };

      // Add optional fields if provided
      if (paymentFormData.reference_number) {
        paymentData.reference_number = paymentFormData.reference_number;
      }
      if (paymentFormData.notes) {
        paymentData.notes = paymentFormData.notes;
      }

      const result = await tuitionService.createPayment(paymentData);

      if (result.success) {
        toast({
          title: 'Success',
          description: result.message || 'Payment created successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        onPaymentClose();
        // Reset form
        setPaymentFormData({
          amount: '',
          payment_date: '',
          payment_method: '',
          reference_number: '',
          notes: '',
          status: 'pending',
        });
        // Refresh tuition data
        const fetchResult = await tuitionService.getTuition(id);
        if (fetchResult.success && fetchResult.data) {
          setTuition(fetchResult.data);
        }
        // Refresh payments data
        const paymentsResult = await tuitionService.getPayments({
          tuition_id: id,
          skip: 0,
          limit: 100,
        });
        if (paymentsResult.success && paymentsResult.data) {
          setPayments(paymentsResult.data);
        }
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to create payment',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Create payment error:', err);
      toast({
        title: 'Error',
        description: 'Failed to create payment. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCreatingPayment(false);
    }
  };

  // Initialize payment form with today's date
  const handlePaymentClick = () => {
    const today = new Date().toISOString().split('T')[0];
    setPaymentFormData(prev => ({
      ...prev,
      payment_date: today,
    }));
    onPaymentOpen();
  };

  if (loading) {
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <Center minH="400px">
          <Spinner size="xl" color="brand.500" />
        </Center>
      </Box>
    );
  }

  if (error || !tuition) {
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <Card p='40px'>
          <Flex direction='column' align='center' gap='20px'>
            <Text color={textColor} fontSize='xl' fontWeight='600'>
              {error || 'Tuition not found'}
            </Text>
            <Button onClick={() => navigate('/admin/tuition')} leftIcon={<Icon as={MdArrowBack} />}>
              Go Back
            </Button>
          </Flex>
        </Card>
      </Box>
    );
  }

  const statusColorScheme = 
    tuition.status === 'paid' ? 'green' : 
    tuition.status === 'pending' ? 'yellow' : 
    tuition.status === 'overdue' ? 'red' : 'gray';

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Flex direction='column' gap='20px'>
        {/* Back, Edit, Delete, and Create Payment Buttons */}
        <Flex justify='space-between' align='center' mb='10px' flexWrap='wrap' gap='10px'>
          <Button
            onClick={() => navigate('/admin/tuition')}
            leftIcon={<Icon as={MdArrowBack} />}
            variant='ghost'>
            Back to Tuitions
          </Button>
          <Flex gap='10px' flexWrap='wrap'>
            <Button
              onClick={handlePaymentClick}
              leftIcon={<Icon as={MdAdd} />}
              colorScheme='green'
              variant='solid'>
              Create Payment
            </Button>
            <Button
              onClick={handleEditClick}
              leftIcon={<Icon as={MdEdit} />}
              colorScheme='blue'
              variant='solid'>
              Edit Tuition
            </Button>
            <Button
              onClick={onDeleteOpen}
              leftIcon={<Icon as={MdDelete} />}
              colorScheme='red'
              variant='solid'>
              Delete Tuition
            </Button>
          </Flex>
        </Flex>

        {/* Tuition Header */}
        <Card p='30px'>
          <Flex direction='column' gap='20px'>
            <Flex justify='space-between' align='center' flexWrap='wrap' gap='15px'>
              <Flex direction='column'>
                <Text color={textColor} fontSize='2xl' fontWeight='700' mb='5px'>
                  Tuition Details
                </Text>
                <Text color={textColorSecondary} fontSize='sm'>
                  ID: {tuition.id}
                </Text>
              </Flex>
              <Badge 
                colorScheme={statusColorScheme} 
                bg={bgStatus} 
                fontSize="md" 
                fontWeight="700" 
                px="15px" 
                py="5px"
              >
                {formatStatus(tuition.status)}
              </Badge>
            </Flex>
            <Divider borderColor={borderColor} />
          </Flex>
        </Card>

        {/* Student Information */}
        <Card p='30px'>
          <Flex align='center' gap='10px' mb='20px'>
            <Icon as={MdPerson} color={textColor} fontSize='xl' />
            <Text color={textColor} fontSize='xl' fontWeight='600'>
              Student Information
            </Text>
          </Flex>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing='20px'>
            <Box>
              <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                Student Name
              </Text>
              <Text color={textColor} fontSize='md' fontWeight='600'>
                {tuition.student_name}
              </Text>
            </Box>
            <Box>
              <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                Matricule
              </Text>
              <Text color={textColor} fontSize='md' fontWeight='600'>
                {tuition.student_matricule}
              </Text>
            </Box>
            <Box>
              <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                Center
              </Text>
              <Text color={textColor} fontSize='md' fontWeight='600'>
                {tuition.student_center}
              </Text>
            </Box>
            <Box>
              <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                Student ID
              </Text>
              <Text color={textColor} fontSize='md' fontWeight='600'>
                {tuition.student_id}
              </Text>
            </Box>
          </SimpleGrid>
        </Card>

        {/* Tuition Details */}
        <Card p='30px'>
          <Flex align='center' gap='10px' mb='20px'>
            <Icon as={MdSchool} color={textColor} fontSize='xl' />
            <Text color={textColor} fontSize='xl' fontWeight='600'>
              Tuition Information
            </Text>
          </Flex>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing='20px'>
            <Box>
              <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                Type
              </Text>
              <Text color={textColor} fontSize='md' fontWeight='600'>
                {tuition.type}
              </Text>
            </Box>
            <Box>
              <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                Prep Session
              </Text>
              <Text color={textColor} fontSize='md' fontWeight='600'>
                {tuition.prepa_session}
              </Text>
            </Box>
            <Box>
              <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                Description
              </Text>
              <Text color={textColor} fontSize='md' fontWeight='600'>
                {tuition.description || 'N/A'}
              </Text>
            </Box>
            <Box>
              <Flex align='center' gap='5px' mb='5px'>
                <Icon as={MdCalendarToday} color={textColorSecondary} fontSize='sm' />
                <Text color={textColorSecondary} fontSize='sm'>
                  Due Date
                </Text>
              </Flex>
              <Text color={textColor} fontSize='md' fontWeight='600'>
                {formatDate(tuition.due_date)}
              </Text>
            </Box>
          </SimpleGrid>
        </Card>

        {/* Financial Information */}
        <Card p='30px'>
          <Flex align='center' gap='10px' mb='20px'>
            <Icon as={MdAttachMoney} color={textColor} fontSize='xl' />
            <Text color={textColor} fontSize='xl' fontWeight='600'>
              Financial Information
            </Text>
          </Flex>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing='20px'>
            <Box>
              <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                Tuition Amount
              </Text>
              <Text color={textColor} fontSize='lg' fontWeight='700'>
                {formatCurrency(tuition.amount)}
              </Text>
            </Box>
            <Box>
              <Flex align='center' gap='5px' mb='5px'>
                <Icon as={MdPayment} color='green.500' fontSize='sm' />
                <Text color={textColorSecondary} fontSize='sm'>
                  Total Paid
                </Text>
              </Flex>
              <Text color='green.500' fontSize='lg' fontWeight='700'>
                {formatCurrency(tuition.total_paid)}
              </Text>
            </Box>
            <Box>
              <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                Remaining Balance
              </Text>
              <Text 
                color={tuition.remaining_balance < 0 ? 'green.500' : tuition.remaining_balance > 0 ? 'red.500' : textColor} 
                fontSize='lg' 
                fontWeight='700'
              >
                {formatCurrency(tuition.remaining_balance)}
              </Text>
            </Box>
            <Box>
              <Flex align='center' gap='5px' mb='5px'>
                <Icon 
                  as={tuition.status === 'paid' ? MdCheckCircle : MdPendingActions} 
                  color={statusColorScheme === 'green' ? 'green.500' : statusColorScheme === 'yellow' ? 'orange.500' : 'secondaryGray.500'} 
                  fontSize='sm' 
                />
                <Text color={textColorSecondary} fontSize='sm'>
                  Status
                </Text>
              </Flex>
              <Badge 
                colorScheme={statusColorScheme} 
                bg={bgStatus} 
                fontSize="sm" 
                fontWeight="700" 
                px="10px" 
                py="3px"
              >
                {formatStatus(tuition.status)}
              </Badge>
            </Box>
          </SimpleGrid>
        </Card>

        {/* Timestamps */}
        <Card p='30px'>
          <Text color={textColor} fontSize='xl' fontWeight='600' mb='20px'>
            Timestamps
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing='20px'>
            <Box>
              <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                Created At
              </Text>
              <Text color={textColor} fontSize='md' fontWeight='500'>
                {formatDate(tuition.created_at)}
              </Text>
            </Box>
            <Box>
              <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                Last Updated
              </Text>
              <Text color={textColor} fontSize='md' fontWeight='500'>
                {formatDate(tuition.updated_at)}
              </Text>
            </Box>
          </SimpleGrid>
        </Card>

        {/* Payments History */}
        <Box>
          <Flex align='center' gap='10px' mb='20px'>
            <Icon as={MdPayment} color={textColor} fontSize='xl' />
            <Text color={textColor} fontSize='xl' fontWeight='600'>
              Payment History
            </Text>
          </Flex>
          {paymentsLoading ? (
            <Card p='40px'>
              <Center minH="200px">
                <Spinner size="lg" color="brand.500" />
              </Center>
            </Card>
          ) : (
            <PaymentsTable tableData={payments} />
          )}
        </Box>
      </Flex>

      {/* Edit Modal */}
      <Modal isOpen={isEditOpen} onClose={onEditClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent bg={bg}>
          <ModalHeader color={textColor} fontSize="2xl" fontWeight="700">
            Edit Tuition
          </ModalHeader>
          <ModalCloseButton color={textColor} />
          <ModalBody pb="24px">
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
              <FormControl>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Amount
                </FormLabel>
                <Input
                  variant='main'
                  fontSize='sm'
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  placeholder="Enter tuition amount"
                  mb='8px'
                />
              </FormControl>

              <FormControl>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Due Date
                </FormLabel>
                <Input
                  variant='main'
                  fontSize='sm'
                  type="date"
                  name="due_date"
                  value={formData.due_date}
                  onChange={handleInputChange}
                  mb='8px'
                />
              </FormControl>

              <FormControl>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Prep Session
                </FormLabel>
                <Input
                  variant='main'
                  fontSize='sm'
                  type="text"
                  name="prepa_session"
                  value={formData.prepa_session}
                  onChange={handleInputChange}
                  placeholder="e.g., 2025-2026"
                  mb='8px'
                />
              </FormControl>

              <FormControl>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Type
                </FormLabel>
                <Select
                  variant='main'
                  fontSize='sm'
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  placeholder="Select type"
                  mb='8px'
                >
                  <option value="PHASE 1">PHASE 1</option>
                  <option value="PHASE 2">PHASE 2</option>
                  <option value="PHASE 3">PHASE 3</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Status
                </FormLabel>
                <Select
                  variant='main'
                  fontSize='sm'
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  placeholder="Select status"
                  mb='8px'
                >
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="overdue">Overdue</option>
                </Select>
              </FormControl>

              <FormControl gridColumn={{ base: '1', md: '1 / 3' }}>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Description
                </FormLabel>
                <Textarea
                  variant='main'
                  fontSize='sm'
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter description"
                  rows={4}
                  mb='8px'
                />
              </FormControl>
            </SimpleGrid>
          </ModalBody>

          <ModalFooter>
            <Button
              variant='light'
              fontWeight='500'
              mr={3}
              onClick={onEditClose}
              isDisabled={updating}>
              Cancel
            </Button>
            <Button
              variant='brand'
              fontWeight='500'
              onClick={handleUpdate}
              isLoading={updating}
              loadingText="Updating">
              Update Tuition
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg={bg}>
            <AlertDialogHeader fontSize="xl" fontWeight="700" color={textColor}>
              Delete Tuition
            </AlertDialogHeader>

            <AlertDialogBody>
              <Text color={textColor} fontSize="md" mb="10px">
                Are you sure you want to delete this tuition? This action cannot be undone.
              </Text>
              {tuition?.total_validated_payments > 0 && (
                <Box
                  mt="15px"
                  p="12px"
                  borderRadius="8px"
                  bg="red.50"
                  border="1px solid"
                  borderColor="red.200">
                  <Text color="red.600" fontSize="sm" fontWeight="500">
                    ⚠️ Note: Tuitions with validated payments cannot be deleted.
                  </Text>
                </Box>
              )}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                variant='light'
                fontWeight='500'
                onClick={onDeleteClose}
                isDisabled={deleting}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                variant='solid'
                fontWeight='500'
                onClick={handleDelete}
                ml={3}
                isLoading={deleting}
                loadingText="Deleting">
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Create Payment Modal */}
      <Modal isOpen={isPaymentOpen} onClose={onPaymentClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent bg={bg}>
          <ModalHeader color={textColor} fontSize="2xl" fontWeight="700">
            Create Payment
          </ModalHeader>
          <ModalCloseButton color={textColor} />
          <ModalBody pb="24px">
            {tuition && (
              <Box mb="20px" p="15px" bg={paymentInfoBg} borderRadius="12px" border="1px solid" borderColor={paymentInfoBorder}>
                <Text color={textColor} fontSize="sm" fontWeight="600" mb="5px">
                  Remaining Balance: {formatCurrency(tuition.remaining_balance)}
                </Text>
                <Text color={textColorSecondary} fontSize="xs">
                  Payment amount cannot exceed this balance
                </Text>
              </Box>
            )}
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
              <FormControl isRequired isInvalid={!!paymentErrors.amount}>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Amount<Text color='red.500'>*</Text>
                </FormLabel>
                <Input
                  variant='main'
                  fontSize='sm'
                  type="number"
                  name="amount"
                  value={paymentFormData.amount}
                  onChange={handlePaymentInputChange}
                  placeholder="Enter payment amount"
                  mb='8px'
                />
                {paymentErrors.amount && (
                  <Text color='red.500' fontSize='xs' ms='4px' mt='4px'>
                    {paymentErrors.amount}
                  </Text>
                )}
              </FormControl>

              <FormControl isRequired isInvalid={!!paymentErrors.payment_date}>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Payment Date<Text color='red.500'>*</Text>
                </FormLabel>
                <Input
                  variant='main'
                  fontSize='sm'
                  type="date"
                  name="payment_date"
                  value={paymentFormData.payment_date}
                  onChange={handlePaymentInputChange}
                  mb='8px'
                />
                {paymentErrors.payment_date && (
                  <Text color='red.500' fontSize='xs' ms='4px' mt='4px'>
                    {paymentErrors.payment_date}
                  </Text>
                )}
              </FormControl>

              <FormControl isRequired isInvalid={!!paymentErrors.payment_method}>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Payment Method<Text color='red.500'>*</Text>
                </FormLabel>
                <Select
                  variant='main'
                  fontSize='sm'
                  name="payment_method"
                  value={paymentFormData.payment_method}
                  onChange={handlePaymentInputChange}
                  placeholder="Select payment method"
                  mb='8px'
                >
                  <option value="cash">Cash</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="mobile_money">Mobile Money</option>
                  <option value="check">Check</option>
                  <option value="other">Other</option>
                </Select>
                {paymentErrors.payment_method && (
                  <Text color='red.500' fontSize='xs' ms='4px' mt='4px'>
                    {paymentErrors.payment_method}
                  </Text>
                )}
              </FormControl>

              <FormControl>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Status
                </FormLabel>
                <Select
                  variant='main'
                  fontSize='sm'
                  name="status"
                  value={paymentFormData.status}
                  onChange={handlePaymentInputChange}
                  mb='8px'
                >
                  <option value="pending">Pending</option>
                  <option value="validated">Validated</option>
                  <option value="rejected">Rejected</option>
                </Select>
              </FormControl>

              <FormControl gridColumn={{ base: '1', md: '1 / 3' }}>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Reference Number
                </FormLabel>
                <Input
                  variant='main'
                  fontSize='sm'
                  type="text"
                  name="reference_number"
                  value={paymentFormData.reference_number}
                  onChange={handlePaymentInputChange}
                  placeholder="Transaction reference or receipt number"
                  mb='8px'
                />
              </FormControl>

              <FormControl gridColumn={{ base: '1', md: '1 / 3' }}>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Notes
                </FormLabel>
                <Textarea
                  variant='main'
                  fontSize='sm'
                  name="notes"
                  value={paymentFormData.notes}
                  onChange={handlePaymentInputChange}
                  placeholder="Additional notes (optional)"
                  rows={3}
                  mb='8px'
                />
              </FormControl>
            </SimpleGrid>
          </ModalBody>

          <ModalFooter>
            <Button
              variant='light'
              fontWeight='500'
              mr={3}
              onClick={onPaymentClose}
              isDisabled={creatingPayment}>
              Cancel
            </Button>
            <Button
              variant='brand'
              fontWeight='500'
              onClick={handleCreatePayment}
              isLoading={creatingPayment}
              loadingText="Creating">
              Create Payment
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

